import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from '../plugins/axios'
import {
  getDefaultModuleRoute,
  isFeatureModule,
  type FeatureModule,
} from '../types/featureModules'

export interface User {
  id: number
  name: string
  email: string
  is_admin: boolean
  modules: FeatureModule[]
}

export interface AuthCredentials {
  name?: string
  email: string
  password: string
  password_confirmation?: string
}

export interface ResetPasswordData {
  email: string
  password: string
  password_confirmation: string
  token: string
}

type ActionSuccess<T = void> = { success: true; data?: T; message?: string; errors?: Record<string, string[]> }
type ActionFailure = { success: false; error: string; errors?: Record<string, string[]> }
export type ActionResult<T = void> = ActionSuccess<T> | ActionFailure

// Helper functions for localStorage
function getStoredUser(): User | null {
  const stored = localStorage.getItem('user')
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)
    return {
      ...parsed,
      is_admin: Boolean(parsed.is_admin),
      modules: Array.isArray(parsed.modules) ? parsed.modules.filter(isFeatureModule) : [],
    }
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

function getStoredToken(): string | null {
  return localStorage.getItem('auth_token')
}

function setStoredUser(user: User | null): void {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user))
  } else {
    localStorage.removeItem('user')
  }
}

function setStoredToken(token: string | null): void {
  if (token) {
    localStorage.setItem('auth_token', token)
  } else {
    localStorage.removeItem('auth_token')
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(getStoredUser())
  const token = ref<string | null>(getStoredToken())
  const loading = ref(false)
  const error = ref<string | null>(null)
  const permissionsSynced = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => !!user.value?.is_admin)
  const currentUser = computed(() => user.value)
  const defaultRoute = computed(() => getDefaultModuleRoute(
    user.value?.modules || [],
    Boolean(user.value?.is_admin),
  ))

  // Actions
  function setUser(userData: User): void {
    const normalizedUser: User = {
      ...userData,
      is_admin: Boolean(userData.is_admin),
      modules: Array.isArray(userData.modules) ? userData.modules.filter(isFeatureModule) : [],
    }
    user.value = normalizedUser
    setStoredUser(normalizedUser)
  }

  function setToken(newToken: string): void {
    token.value = newToken
    setStoredToken(newToken)
  }

  function clearAuth(): void {
    user.value = null
    token.value = null
    setStoredUser(null)
    setStoredToken(null)
    permissionsSynced.value = false
  }

  function canAccessModule(module: FeatureModule): boolean {
    return Boolean(user.value?.is_admin || user.value?.modules.includes(module))
  }

  async function register(credentials: AuthCredentials): Promise<ActionResult<User>> {
    loading.value = true
    error.value = null

    try {
      const response = await axios.post('/api/register', credentials)
      const { data } = response.data

      const newUser: User = {
        id: data.id,
        name: data.name,
        email: data.email,
        is_admin: data.is_admin || false,
        modules: Array.isArray(data.modules) ? data.modules.filter(isFeatureModule) : [],
      }

      setUser(newUser)
      setToken(data.token)
      permissionsSynced.value = true

      return { success: true, data: newUser }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Registration failed'
      error.value = message
      return {
        success: false,
        error: message,
        errors: err.response?.data?.errors || {}
      }
    } finally {
      loading.value = false
    }
  }

  async function login(credentials: AuthCredentials): Promise<ActionResult<User>> {
    loading.value = true
    error.value = null

    try {
      const response = await axios.post('/api/login', credentials)
      const { data } = response.data

      const newUser: User = {
        id: data.id,
        name: data.name,
        email: data.email,
        is_admin: data.is_admin || false,
        modules: Array.isArray(data.modules) ? data.modules.filter(isFeatureModule) : [],
      }

      setUser(newUser)
      setToken(data.token)
      permissionsSynced.value = true

      return { success: true, data: newUser }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Login failed'
      error.value = message
      return {
        success: false,
        error: message,
        errors: err.response?.data?.errors || {}
      }
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    loading.value = true

    try {
      await axios.post('/api/logout')
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearAuth()
      loading.value = false
    }
  }

  async function fetchUser(): Promise<ActionResult> {
    if (!token.value) return { success: false, error: 'No token' }

    try {
      const response = await axios.get('/api/user')
      setUser(response.data.data)
      permissionsSynced.value = true
      return { success: true }
    } catch (err) {
      clearAuth()
      return { success: false, error: 'Failed to fetch user' }
    }
  }

  function checkAuth(): boolean {
    return isAuthenticated.value
  }

  async function forgotPassword(email: string): Promise<ActionResult> {
    loading.value = true
    error.value = null

    try {
      const response = await axios.post('/api/forgot-password', { email })
      return { success: true, message: response.data.message }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to send reset link'
      error.value = message
      return {
        success: false,
        error: message,
        errors: err.response?.data?.errors || {},
      }
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(data: ResetPasswordData): Promise<ActionResult> {
    loading.value = true
    error.value = null

    try {
      const response = await axios.post('/api/reset-password', data)
      return { success: true, message: response.data.message }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to reset password'
      error.value = message
      return {
        success: false,
        error: message,
        errors: err.response?.data?.errors || {},
      }
    } finally {
      loading.value = false
    }
  }

  // Reset function - clears reactive state but keeps localStorage
  function $reset(): void {
    user.value = getStoredUser()
    token.value = getStoredToken()
    loading.value = false
    error.value = null
    permissionsSynced.value = false
  }

  // Full reset including localStorage
  function $resetFull(): void {
    user.value = null
    token.value = null
    loading.value = false
    error.value = null
    permissionsSynced.value = false
    setStoredUser(null)
    setStoredToken(null)
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    permissionsSynced,
    // Getters
    isAuthenticated,
    isAdmin,
    currentUser,
    defaultRoute,
    // Actions
    setUser,
    setToken,
    clearAuth,
    canAccessModule,
    register,
    login,
    logout,
    fetchUser,
    checkAuth,
    forgotPassword,
    resetPassword,
    $reset,
    $resetFull,
  }
})
